// biome-ignore lint/style/useFilenamingConvention: TanStack Router dynamic route parameter

import { createFileRoute } from "@tanstack/react-router";
import { Suspense } from "react";

import { hasProjectPermission } from "@/app/capabilities/api/check-permission";
import { projectCapabilitiesQueryOptions } from "@/app/capabilities/api/get-capabilities";
import { PROJECT } from "@/app/capabilities/permissions";
import { hooksQueryOptions } from "@/app/logic-extensions/api/get-hooks";
import { pluginsQueryOptions } from "@/app/logic-extensions/api/get-plugins";
import { LogicExtensionsSkeleton } from "@/app/logic-extensions/components/logic-extensions-skeleton";
import { EditPluginPage } from "@/app/logic-extensions/pages/edit-plugin-page";
import { requireProjectPermission } from "@/lib/route-guards";

export const Route = createFileRoute(
  "/_authenticated/orgs/$organizationId/projects/$projectId/contextual-access/$pluginId"
)({
  component: () => (
    <Suspense fallback={<LogicExtensionsSkeleton />}>
      <EditPluginPage />
    </Suspense>
  ),
  async beforeLoad(ctx) {
    // Gate on read, not update: the editor is reachable for anyone who can view
    // contextual access, and update is enforced per binding by the row's Edit
    // action (PROJECT.plugins.update for project-bound, ORG.plugins.update for
    // tenant-bound) plus the backend. Requiring project update here would 403 an
    // org admin editing a tenant-bound plugin.
    await requireProjectPermission(ctx, PROJECT.plugins.read);
    ctx.context.queryClient.prefetchQuery(
      pluginsQueryOptions({ binding: "project", scope: ctx.params })
    );
    // Prefetch hooks only when the caller can read them — the editor loads hooks
    // conditionally (hooks_read), so an unreadable prefetch would just 403.
    const capabilities = await ctx.context.queryClient.ensureQueryData(
      projectCapabilitiesQueryOptions(ctx.params)
    );
    if (hasProjectPermission(capabilities.authz, PROJECT.hooks.read)) {
      ctx.context.queryClient.prefetchQuery(
        hooksQueryOptions({ binding: "project", scope: ctx.params })
      );
    }
  },
  loader: ({ params: { pluginId } }) => ({
    pluginId,
    crumb: "Edit System",
  }),
});
